// components/InviteCardLarge.js
//
// The big home card: the cover at full width, and a footer tinted by the
// cover's own colours. The web half of the app's InviteCardLarge, down to the
// 1.15 aspect ratio and the three-stop gradient.
//
// ── The tint ───────────────────────────────────────────────────────────────
//
// The footer gradient comes from the cover's blurhash, sampled in bands rather
// than averaged (shared/eventPalette — the same generated module the app uses,
// so both surfaces compute the identical colours from the identical string).
// A single average of a photo is usually mud; the bands keep the sky above and
// the grass below, so the footer reads as a continuation of the picture rather
// than a coloured box under it.
//
// Events uploaded before cover_blurhash existed have none and fall back to the
// flat surface colour, which is what every card looked like before.
//
// ── Why the still, never the GIF ───────────────────────────────────────────
//
// resolveEventThumbnail, not resolveEventCover: a feed of decoding GIFs is the
// fastest way to make scrolling stutter on a mid-range phone. The animation
// plays on the invitation page, where there is one of them.

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { IoStar, IoCheckmark, IoCalendar, IoDocumentOutline } from 'react-icons/io5';
import { formatEventWhen, formatEventLocation } from '../shared/formatEvent';
import { isOpenDatePoll } from '../shared/sortEvents';
import { resolveEventThumbnail, coverBlurhash } from '../shared/eventCover';
import { gradientForBlurhash } from '../shared/eventPalette';
import './InviteCardLarge.scss';

/** Matches $background in variables.scss — what the tint is mixed toward. */
const BACKGROUND = '#0B0B0F';

const FALLBACK_COVER = '/images/events/1.jpg';

function Pill({ label, tone, Glyph }) {
  return (
    <span className={`invite-pill invite-pill--${tone}`}>
      {Glyph ? <Glyph /> : null}
      {label}
    </span>
  );
}

export default function InviteCardLarge({ event, isPast, onOpen }) {
  const when = formatEventWhen(event);
  const where = formatEventLocation(event);
  const needsVote = isOpenDatePoll(event);
  const cover = resolveEventThumbnail(event) || FALLBACK_COVER;

  const footer = useMemo(() => {
    const stops = gradientForBlurhash(coverBlurhash(event), BACKGROUND, 3);
    if (!stops) return undefined;
    return {
      backgroundImage: `linear-gradient(to bottom, ${stops[0]} 0%, ${stops[1]} 60%, ${stops[2]} 100%)`,
    };
  }, [event]);

  return (
    <motion.article
      className={`invite-card ${isPast ? 'is-past' : ''}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <button type="button" className="invite-card-hit" onClick={onOpen}>
        {/* The whole card is one control. An accessible name built from the
            title and the date means a screen reader announces the invitation,
            not "button, image". */}
        <span className="sr-only">
          {event.title}
          {when ? `, ${when}` : ''}
        </span>

        <span className="invite-card-cover">
          <img src={cover} alt="" loading="lazy" />

          {/* Badges sit on the image, so they carry their own scrim. */}
          <span className="invite-card-badges">
            {(event.is_hosting ?? event.is_host) ? (
              <Pill label="Hosting" tone="gold" Glyph={IoStar} />
            ) : null}
            {/* Only its host ever sees a draft, so this is a reminder, not a
                label for guests. */}
            {event.status === 'draft' ? (
              <Pill label="Draft" tone="warning" Glyph={IoDocumentOutline} />
            ) : null}
            {needsVote ? <Pill label="Pick a date" tone="warning" Glyph={IoCalendar} /> : null}
            {event.is_going || event.rsvp_status === 'going' ? (
              <Pill label="Going" tone="success" Glyph={IoCheckmark} />
            ) : null}
          </span>
        </span>

        <span className="invite-card-foot" style={footer}>
          <span className="invite-card-title">{event.title}</span>
          {when ? <span className="invite-card-when">{when}</span> : null}
          {where ? <span className="invite-card-where">{where}</span> : null}
        </span>
      </button>
    </motion.article>
  );
}
